import { useState, useRef, useEffect, useCallback } from "react";
import { RealtimeSession, tool } from "@openai/agents/realtime";
import { demoMeetingBookerAgent } from "@callcow/agents";
import { api } from "../trpc/react";

export function useInBrowserCall() {
  const [isActive, setIsActive] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const sessionRef = useRef<RealtimeSession | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  const { data: tokenData, isLoading: tokenLoading } =
    api.calling.getToken.useQuery(undefined, {
      enabled: isActive && !isConnected,
    });

  const disconnectSession = useCallback(() => {
    // Stop the microphone if session exists
    if (sessionRef.current) {
      sessionRef.current.close();
      // Setting to null to cleanup
      sessionRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsConnected(false);
    setIsMuted(false);
  }, []);

  const endCall = useCallback(() => {
    disconnectSession();
    setIsActive(false);
  }, [disconnectSession]);

  const connectRealtimeSession = useCallback(async () => {
    if (!tokenData?.token || isConnected) return;

    try {
      // Request microphone access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      mediaStreamRef.current = mediaStream;
      audioContextRef.current = new AudioContext();

      const agent = demoMeetingBookerAgent;
      agent.tools.push(
        tool({
          name: "hang_up",
          description: "End the call and disconnect the session",
          parameters: {
            type: "object",
            properties: {},
            required: [],
            additionalProperties: false,
          },
          execute: async () => {
            endCall();
            return "Call ended";
          },
        })
      );
      // Create realtime session
      const session = new RealtimeSession(agent, {
        model: "gpt-realtime",
        config: { audio: { output: { voice: "verse" } } },
      });

      sessionRef.current = session;

      await session.connect({ apiKey: tokenData.token });

      setIsConnected(true);
    } catch (error) {
      console.error("Failed to connect:", error);
      disconnectSession();
    }
  }, [tokenData, isConnected, disconnectSession, endCall]);

  const toggleMute = useCallback(() => {
    if (mediaStreamRef.current) {
      const audioTracks = mediaStreamRef.current.getAudioTracks();
      audioTracks.forEach((track) => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const startCall = useCallback(() => {
    setIsActive(true);
  }, []);

  // Auto-connect when token is available
  useEffect(() => {
    if (isActive && tokenData?.token && !isConnected && !tokenLoading) {
      connectRealtimeSession();
    }
  }, [isActive, tokenData, isConnected, tokenLoading, connectRealtimeSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectSession();
    };
  }, [disconnectSession]);

  return {
    // State
    isActive,
    isConnected,
    isMuted,
    isConnecting: tokenLoading,

    // Actions
    startCall,
    endCall,
    toggleMute,
  };
}
